import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Clickable } from './Clickable';
import { SvgIcon } from './SvgIcon';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';

export interface EmptyStateProps {
  icon?: string;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

function createEmptyStateStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: appTheme.spacing.xl,
    },
    title: {
      ...appTheme.typography.h3,
      color: c.text,
      marginTop: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
    },
    message: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      textAlign: 'center',
      marginBottom: appTheme.spacing.lg,
    },
    actionButton: {
      backgroundColor: c.primary,
      paddingHorizontal: appTheme.spacing.xl,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
    },
    actionButtonText: {
      ...appTheme.typography.body,
      color: c.onPrimary,
      fontWeight: '600',
    },
  });
}

export const EmptyState: React.FC<EmptyStateProps> = ({ icon = 'file-tray-outline', title, message, actionLabel, onAction }) => {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createEmptyStateStyles(colors), [colors]);
  return (
    <View style={styles.container}>
      <SvgIcon name={icon} size={64} color={colors.textLight} />
      <Text style={styles.title}>{title}</Text>
      <Text style={styles.message}>{message}</Text>
      {actionLabel && onAction && (
        <Clickable style={styles.actionButton} onPress={onAction} accessibilityLabel={actionLabel} accessibilityRole="button">
          <Text style={styles.actionButtonText}>{actionLabel}</Text>
        </Clickable>
      )}
    </View>
  );
};
